// Start/finish alignment detection.
//
// Two sessions at the same circuit can have their laps timed from different
// physical points (each logger's own beacon / GPS start line). Every trace
// point's dist_m is measured from its own lap start, so comparison views
// (SpeedTrace overlay, Head-to-Head corner deltas) silently misalign when the
// origins differ. These helpers measure how far a session's lap-start point
// sits from a reference coordinate — the track's canonical start_finish, or
// Driver 1's own lap start when the track has none stored — using only
// already-loaded lap traces (points carry lat/lon; no extra fetches).

// Offsets above this are a real misplaced beacon, not GPS noise or transponder
// placement (those are tens of metres at worst).
export const SF_MISMATCH_WARN_M = 120;

export function haversineM(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLon = (lon2 - lon1) * rad;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// First GPS point of any loaded timed-lap trace — i.e. where this session's
// laps start on track. Out/in-laps are skipped (their "start" is the session
// edge, not an S/F crossing). Returns null until a timed-lap trace is loaded.
export function lapStartPoint(laps, lapTraces) {
  for (const lap of laps || []) {
    if (lap.is_outlap || lap.is_inlap) continue;
    const p = lapTraces?.[lap.index]?.points?.[0];
    if (p && Number.isFinite(p.lat) && Number.isFinite(p.lon)) {
      return { lat: p.lat, lon: p.lon };
    }
  }
  return null;
}

// Distance (m) from a session's lap-start point to a reference {lat, lon},
// or null when either side isn't available yet.
export function sfOffsetM(laps, lapTraces, ref) {
  if (!ref || !Number.isFinite(ref.lat) || !Number.isFinite(ref.lon)) return null;
  const p = lapStartPoint(laps, lapTraces);
  if (!p) return null;
  return haversineM(p.lat, p.lon, ref.lat, ref.lon);
}
